import {
  ChevronDownIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useState } from 'react'
import trackEvents from '../../constants/track-events'
import { trackEvent } from '../../helpers/analytics'
import DeleteSpace from '../actions/delete-space'
import FullReset from '../actions/full-reset'
import SendReminder from '../actions/send-reminder'
import SetSpaceState from '../actions/set-space-state'
import CustomerEffortScore from '../customer-effort-score'
import Dropdown from '../tailwind/dropdown'

// Gesperrtes Talent (Talentspeicher, D9). Status setzen und Löschen (DSGVO)
// bleiben, alles andere würde Ergebnisse zeigen oder das Talent erneut
// einladen.
export const LOCKED_TALENT_ACTIONS = Object.freeze([
  'SET_SPACE_STATE',
  'DELETE_SPACE'
])

const SpaceActionsDropdown = ({
  space,
  refetchSpace,
  career,
  spaceActions,
  canExportPdfBundle,
  onOpenPdfBundle,
  restrictedActions
}) => {
  const { t } = useTranslation()
  const [isCustomerEffortScoreVisible, setIsCustomerEffortScoreVisible] =
    useState(false)

  const navigate = useNavigate()

  const {
    actions: actionsFromHook,
    displaySendReminder,
    setDisplaySendReminder,
    displaySetSpaceState,
    setDisplaySetSpaceState,
    displayDeleteSpace,
    setDisplayDeleteSpace,
    displayFullReset,
    setDisplayFullReset
  } = spaceActions

  const { SEND_REMINDER, SET_SPACE_STATE, DELETE_SPACE, FULL_RESET } =
    actionsFromHook.reduce((acc, curr) => {
      acc[curr.key] = curr
      return acc
    }, {})

  // Der Export betrifft Profile und Dokumente, also die ganze Seite. Deshalb
  // steht er hier und nicht nur unter ANHAENGE.
  const PDF_BUNDLE = canExportPdfBundle && {
    key: 'PDF_BUNDLE',
    label: t('pages.talent.attachments_bundle_action'),
    available: true,
    icons: { solid: DocumentArrowDownIcon },
    onClick: onOpenPdfBundle
  }

  // Ohne restrictedActions bleiben alle Aktionen wie bisher
  const isAllowed = (key) =>
    !Array.isArray(restrictedActions) || restrictedActions.includes(key)

  const actions = [
    space.via === 'EMAIL' && SEND_REMINDER,
    SET_SPACE_STATE,
    PDF_BUNDLE,
    DELETE_SPACE,
    FULL_RESET
  ]
    .filter((item) => item && isAllowed(item.key))
    // Bringt eine Aktion einen eigenen Hinweis mit (archivierte Stelle, D16),
    // bleibt sie gedimmt und das Dropdown ruft beim Klick
    // handleActionNotAvailable auf, wie in der Massenaktionsleiste
    .map((action) => ({ ...action, Icon: action.icons.solid }))

  return (
    <>
      <Dropdown
        MenuButtonX={() => (
          <div className='flex rounded-full bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'>
            {t('space_actions.button_title')}
            <ChevronDownIcon
              className='ml-1 h-5 w-5 text-white'
              aria-hidden='true'
            />
          </div>
        )}
        actions={actions}
      />
      <SetSpaceState
        spaces={[space]}
        updateHandler={refetchSpace}
        open={displaySetSpaceState}
        setOpen={setDisplaySetSpaceState}
        setIsCustomerEffortScoreVisible={setIsCustomerEffortScoreVisible}
      />
      <CustomerEffortScore
        open={isCustomerEffortScoreVisible}
        setOpen={setIsCustomerEffortScoreVisible}
        context='TALENT-STATE'
      />
      {isAllowed('SEND_REMINDER') && (
        <SendReminder
          space={space}
          refetchSpace={refetchSpace}
          career={career}
          open={displaySendReminder}
          setOpen={setDisplaySendReminder}
        />
      )}
      <DeleteSpace
        spaces={[space]}
        open={displayDeleteSpace}
        setOpen={setDisplayDeleteSpace}
        updateHandler={() => {
          trackEvent(trackEvents.DELETE_TALENT)
          navigate('/talents')
        }}
      />
      {isAllowed('FULL_RESET') && (
        <FullReset
          spaces={[space]}
          open={displayFullReset}
          setOpen={setDisplayFullReset}
          updateHandler={refetchSpace}
        />
      )}
    </>
  )
}

SpaceActionsDropdown.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired,
  career: PropTypes.object,
  spaceActions: PropTypes.object.isRequired,
  canExportPdfBundle: PropTypes.bool,
  onOpenPdfBundle: PropTypes.func,
  // Nur diese Aktions-Keys anbieten, etwa LOCKED_TALENT_ACTIONS
  restrictedActions: PropTypes.arrayOf(PropTypes.string)
}

SpaceActionsDropdown.defaultProps = {
  career: null,
  canExportPdfBundle: false,
  onOpenPdfBundle: () => undefined,
  restrictedActions: null
}

export default SpaceActionsDropdown
